import React, { useState, useEffect } from 'react';
import { endOfMonth, isDate, isValid, format } from "date-fns";
import { useStyles } from '../styles/styles';
import { getCurrency, statesLong } from '../global/utils';
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormLabel from "@mui/material/FormLabel";
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select'
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from '@mui/material/InputAdornment';
import { PatternFormat } from "react-number-format";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { Visa, Mastercard, Discover, Amex } from 'react-pay-icons';
import Checkbox from "@mui/material/Checkbox";
import Link from '@mui/material/Link';
import Popup from '../layout/Popup';
import ShortTandC from "../layout/ShortTandC";
import {useCourt, useRequestUrl} from "../context/contextHooks";
import {IconButton} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Stack from "@mui/material/Stack";
import API from "../http/API";
import { validateCardData } from './paymentFunctions';

const CardData = (props) => {
    const showTitle = props.showTitle;
    const paymentRequest = props.paymentRequest;
    const paymentFields = props.paymentFields;
    const setPaymentFields = props.setPaymentFields;
    const setAcceptTerms = props.setAcceptTerms;
    const checkCardType = props.checkCardType;
    const splitFees = props.splitFees;
    const classes = useStyles;
    const [expDate, setExpDate] = useState(endOfMonth(new Date()));
    const [cardNo, setCardNo] = useState(null);
    const [cardType, setCardType] = useState(null);
    const theme = useTheme();
    const [court,] = useCourt();
    const [requestUrl,] = useRequestUrl();
    const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const creditCardType = require('credit-card-type');
    const [openTandC, setOpenTandC] = useState(false);
    const [name, setName] = useState(paymentRequest.payorName);
    const [open, setOpen] = useState(false);
    const [cvcImg, setCvcImg] = useState(null);
    const [graphicResponse, setGraphicResponse] = useState(null);
    const [, setActionResponse] = useState(null);

    const hasValidationErrors = () => {
        return validateCardData(paymentFields, court) !== '';
    };

    const CardIcon = () => {
        const cardName = cardType ? cardType.type : '';
        switch (cardName) {
            case 'american-express':
                return (
                    <Amex style={{width: '35px'}} />
                );
            case 'visa':
                return (
                    <Visa style={{width: '35px'}} />
                );
            case 'mastercard':
                return (
                    <Mastercard style={{width: '35px'}} />
                );
            case 'discover':
                return (
                    <Discover style={{width: '35px'}} />
                );
            default:
                return null;
        }
    }

    const handleCardNumber = (values) => {
        const val = values.value;
        if (val && val.length > 0) {
            setCardNo(val);
            const cardTypeVal = creditCardType(val);
            setCardType(Array.isArray(cardTypeVal) && cardTypeVal.length > 0 ? cardTypeVal[0] : null);
            if (splitFees && cardTypeVal.length > 0) {
                const cardLength = (cardTypeVal[0].type === 'american-express' ? 15 : 16);
                if (val.length === cardLength) {
                    checkCardType(val, format(expDate, 'MM'), format(expDate, 'yy'));
                }
            }
        }
        setPaymentFields({...paymentFields, cardNumber: val});
    }

    const handleExp = (value) => {
        const val = value;
        if (!val) {
            setExpDate(null);
        } else if (isDate(val) && isValid(val)) {
            const exp = endOfMonth(val);
            setExpDate(exp);
        } else {
            setExpDate(null);
        }
    };

    const handleCvv2 = (values) => {
        const val = values.value;
        setPaymentFields({...paymentFields, cvv2: val});
    }

    const handleName = (e) => {
        const val = e.target.value;
        if (val && val !== paymentRequest.payorName) {
            setPaymentFields({...paymentFields, payorName: val});
        }
    }

    const handleAddress = (e) => {
        const val = e.target.value;
        if (val && val !== paymentRequest.payorAddress) {
            setPaymentFields({...paymentFields, payorAddress: val});
        }
    }

    const handleCity = (e) => {
        const val = e.target.value;
        if (val && val !== paymentRequest.payorCity) {
            setPaymentFields({...paymentFields, payorCity: val});
        }
    }

    const handleZip = (values) => {
        if (values && values.value && values.value.length === 5) {
            const val = values.value;
            if (val !== paymentRequest.billingZip) {
                setPaymentFields({...paymentFields, billingZip: val});
            }
            paymentRequest.billingZip = val;
        }
    }

    const handleEmail = (e) => {
        const val = e.target.value;
        setPaymentFields({...paymentFields, email: val});
    }

    const handlePhone = (values) => {
        if (values?.value.length === 10) {
            const val = values.formattedValue;
            setPaymentFields({ ...paymentFields, phone: val });
        } else if (values.value === "") {
            setPaymentFields({ ...paymentFields, phone: null });
        }
    };

    const updateCardStatus = (newValue) => {
        const exp = newValue ? newValue : expDate;
        if (splitFees && cardNo && cardNo.length >= 15) {
            checkCardType(cardNo, format(expDate, 'MM'), format(exp, 'yy'));
        }
    }

    const closeTandC = (close) => {
        if (close) {
            setOpenTandC(false);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenTandC(true);
        }
    }

    const handleClickOpen = () => {
        fetchCvcGraphic();
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const fetchCvcGraphic = () => {
        if (court.cvc) {
            setGraphicResponse(null);
            const url = `${requestUrl}/getImage/${court.cvc.key}`;
            API.getBinary(url, null, setGraphicResponse, setActionResponse);
        }
    };

    useEffect(() => {
        if (graphicResponse) {
            const file = new Blob([graphicResponse], { type: court.cvc.contentType });
            file.name = court.cvc.fileName;
            setCvcImg(file);
        }
    }, [graphicResponse, court.cvc]);

    useEffect(() => {
        if (expDate && isDate(expDate) && isValid(expDate)) {
            setPaymentFields({
                ...paymentFields,
                expMonth: format(expDate, 'MM'),
                expYear: format(expDate, 'yy'),
                expDate: expDate
            });
        } else {
            setPaymentFields({...paymentFields, expMonth: null, expYear: null});
        }
    }, [expDate])

    useEffect(() => {
        setName(paymentRequest.payorName);
    }, [paymentRequest.payorName]);

    return (
        <>
            {showTitle &&
                <Box sx={{borderBottom: 1, borderColor: 'divider', paddingBottom: '0', marginBottom: '0'}}>
                    <Typography component='h3' variant='h6' sx={classes.title} aria-level="3">Card Payment</Typography>
                </Box>
            }
            { openTandC &&
                <Popup open={openTandC} setDone={closeTandC} title="Terms and Conditions" component={<ShortTandC/>} />
            }
            <Paper variant='body1' elevation={3} sx={smallScreen ? {} : classes.controlPanel}>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} sx={{paddingLeft: '0', paddingTop: smallScreen ? '0' : '1rem'}}>
                        { splitFees ?
                            <Grid container spacing={1}>
                                <Grid item xs={6} style={{padding: '0'}}>
                                    <Typography color='primary' sx={{fontSize: '.9rem', textAlign: 'right'}}>Item Subtotal:</Typography>
                                </Grid>
                                <Grid item xs={6} style={{padding: '0'}}>
                                    <Typography color='textPrimary' sx={{fontSize: '.9rem', textAlign: 'right', paddingRight: '2rem'}}>
                                        {getCurrency(paymentRequest.courtAmount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} style={{padding: '0'}}>
                                    <Typography color='primary' sx={{fontSize: '.9rem', textAlign: 'right'}}>Processing Fee:</Typography>
                                </Grid>
                                <Grid item xs={6} style={{padding: '0'}}>
                                    <Typography color='textPrimary' sx={{fontSize: '.9rem', textAlign: 'right', paddingRight: '2rem'}}>
                                        {getCurrency(paymentRequest.feeAmount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} style={{padding: '0'}}>
                                    <Typography color='primary' sx={{fontSize: '.9rem', textAlign: 'right'}}>Payment Total:</Typography>
                                </Grid>
                                <Grid item xs={6} style={{padding: '0'}}>
                                    <Typography color='textPrimary' sx={{fontSize: '.9rem', textAlign: 'right', paddingRight: '2rem'}}>
                                        {getCurrency(paymentRequest.totalAmount)}
                                    </Typography>
                                </Grid>
                            </Grid>:
                            <>
                                <Typography component='span' variant='subtitle1' color='primary' sx={{fontSize: '1.2rem'}}>
                                    Amount:
                                </Typography>
                                <Typography component='span' variant='subtitle2' color='textPrimary' sx={{fontSize: '1.2rem', marginLeft: '1rem'}}>
                                    {getCurrency(paymentRequest.totalAmount)}
                                </Typography>
                            </>
                        }
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{paddingTop: '0', paddingLeft: smallScreen ? '0' : '.5rem'}}>
                        <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                            <FormLabel htmlFor='name' shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                Name on Card
                            </FormLabel>
                            <TextField
                                id='name'
                                name='name'
                                variant='outlined'
                                fullWidth
                                defaultValue={paymentRequest.payorName}
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    handleName(e);
                                }}
                                inputProps={{sx: classes.textbox, "aria-required": "true"}}
                                aria-invalid={hasValidationErrors()}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{paddingLeft: '0'}}>
                        <FormControl fullWidth sx={{marginLeft: '0'}}>
                            <FormLabel htmlFor='cardNumber' shrink={true} required sx={classes.inputLabel}>Card Number</FormLabel>
                            <PatternFormat
                                id='cardNumber'
                                name='cardNumber'
                                customInput={TextField}
                                InputProps={{
                                    id: 'cardNumber',
                                    sx: classes.numberbox,
                                    startAdornment: (
                                        smallScreen ? null :
                                        <InputAdornment position='start'>
                                            <CardIcon />
                                        </InputAdornment>
                                    ),
                                    inputProps: {"aria-required": "true"}
                                }}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                format={cardType && cardType.type === 'american-express' ? '#### ###### #####' : '#### #### #### ####'}
                                onValueChange={(values) => handleCardNumber(values)}
                                aria-invalid={hasValidationErrors()}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{paddingLeft: smallScreen ? '0' : '.5rem'}}>
                        <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                            <FormLabel htmlFor='cardExp' shrink={true} required sx={classes.inputLabel}>{smallScreen ? 'Exp.' : 'Expires'}</FormLabel>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    name='cardExp'
                                    views={['year', 'month']}
                                    onAccept={(newValue) => updateCardStatus(newValue)}
                                    onChange={(newValue) => handleExp(newValue)}
                                    value={expDate}
                                    format='MM/yy'
                                    disableMaskedInput={false}
                                    slotProps={{
                                        textField: {
                                            id: 'cardExp',
                                            placeholder: 'MM/YY',
                                            sx: {
                                                '& input::placeholder': {
                                                    color: '#767676',
                                                    opacity: 1,
                                                },
                                            },
                                            inputProps: {
                                                sx: classes.textbox,
                                                onBlur: () => updateCardStatus(),
                                                'aria-required': 'true',
                                            },
                                            "aria-invalid": hasValidationErrors()
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{paddingLeft: smallScreen ? '0' : '.5rem'}}>
                        <FormControl sx={{marginLeft: smallScreen ? '0' : '.1rem', width: '90%'}}>
                            <FormLabel htmlFor='cvv2' shrink={true} required sx={classes.inputLabel}>
                                CVC
                            </FormLabel>
                            <PatternFormat
                                id='cvv2'
                                name='cvv2'
                                customInput={TextField}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                format={cardType && cardType.type === 'american-express' ? '####' : '###'}
                                InputProps={{
                                    sx: {...classes.numberbox, paddingRight: '.3rem'},
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={handleClickOpen}
                                                sx={{padding: 0, color: 'royalblue' }}
                                                aria-label="Show CVC information"
                                                aria-haspopup="dialog"
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    inputProps: {"aria-required": "true"}
                                }}
                                onValueChange={(values) => handleCvv2(values)}
                                aria-invalid={hasValidationErrors()}
                            />
                        </FormControl>
                        <Popup open={open} maxWidth='sm' setDone={handleClose} title="Card Verification Code (CVC) Information" component={
                            <Stack spacing={2}>
                                {cvcImg ? (
                                    <>
                                        <Box component='img'
                                             src={window.URL.createObjectURL(cvcImg)}
                                             alt="Example of the back of a credit card with the CVC highlighted as a 3-digit number next to the signature strip."
                                             sx={{
                                                 maxWidth: '100%',
                                                 height: 'auto',
                                                 maxHeight: '250px',
                                                 width: 'auto',
                                                 margin: 'auto',
                                                 objectFit: 'contain'
                                             }}/>
                                        <Typography variant="body2" sx={{textAlign: 'center', mt: 1, px: 2}}>
                                            The Card Verification Code (CVC) is a 3-digit number located on the back of your credit card, typically next to the signature strip. For American Express cards, it is a 4-digit number on the front.
                                        </Typography>
                                    </>
                                ) : null}
                            </Stack>
                        } />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{paddingLeft: '0'}}>
                        <FormControl fullWidth >
                            <FormLabel htmlFor='address' required={!!court?.requireAddress} sx={classes.inputLabel}>Billing Address</FormLabel>
                            <TextField
                                id='address'
                                name='address'
                                variant='outlined'
                                fullWidth
                                defaultValue={paymentRequest.payorAddress}
                                onBlur={handleAddress}
                                onChange={(e) => paymentRequest.payorAddress = e.target.value}
                                inputProps={{
                                    sx: classes.textbox,
                                    "aria-required": !!court?.requireAddress ? "true" : "false"
                                }}
                                aria-invalid="false"
                            />
                        </FormControl>
                    </Grid>
                    { court?.requireAddress &&
                        <>
                            <Grid item xs={12} sm={6} sx={{paddingLeft: smallScreen ? '0' : '.5rem'}}>
                                <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                                    <FormLabel htmlFor='city' required={!!court?.requireAddress} sx={classes.inputLabel}>Billing City</FormLabel>
                                    <TextField
                                        id='city'
                                        name='city'
                                        variant='outlined'
                                        fullWidth
                                        defaultValue={paymentRequest?.payorCity}
                                        onBlur={handleCity}
                                        onChange={(e) => paymentRequest.payorCity = e.target.value}
                                        inputProps={{
                                            sx: classes.textbox,
                                            "aria-required": "true"
                                        }}
                                        aria-invalid="false"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{paddingLeft: '0'}}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor='state' required={!!court?.requireAddress} sx={classes.inputLabel}>Billing State</FormLabel>
                                    <Select
                                        id='state'
                                        name='state'
                                        native
                                        variant='outlined'
                                        fullWidth
                                        defaultValue={paymentRequest?.payorState}
                                        onChange={(e) => paymentRequest.payorState = e.target.value}
                                        sx={{height: '2rem'}}
                                        inputProps={{
                                            id: 'optionSelect',
                                            "aria-required": "true"
                                        }}
                                        aria-invalid="false"
                                    >
                                        <option key='None' value=''>Select your state</option>
                                        {statesLong.map((state) => (
                                            <option key={state.key} value={state.value} style={{fontSize: '.9rem'}}>{state.text}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    }
                    <Grid item xs={12} sm={6} sx={{paddingLeft: smallScreen ? '0' : '.5rem'}}>
                        <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                            <FormLabel htmlFor='billingZip' shrink={true} required={!!court.requireZip} sx={classes.inputLabel}>Billing Zip</FormLabel>
                            <PatternFormat
                                id='billingZip'
                                name='billingZip'
                                customInput={TextField}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                format='#####'
                                InputProps={{
                                    sx: classes.numberbox,
                                    inputProps: {
                                        "aria-required": !!court.requireZip ? "true" : "false"
                                    }
                                }}
                                defaultValue={paymentRequest.billingZip}
                                onValueChange={(values) => handleZip(values)}
                                aria-invalid={hasValidationErrors()}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{paddingLeft: '0'}}>
                        <FormControl fullWidth>
                            <FormLabel htmlFor='email' shrink={true} sx={classes.inputLabel}>Email Address</FormLabel>
                            <TextField
                                id='email'
                                name='email'
                                variant='outlined'
                                fullWidth
                                defaultValue={paymentRequest.email}
                                onBlur={handleEmail}
                                inputProps={{sx: classes.textbox}}
                                aria-invalid={hasValidationErrors()}
                                autoComplete='email'
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{paddingLeft: smallScreen ? '0' : '.5rem'}}>
                        <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                            <FormLabel htmlFor='phone' shrink={true} sx={classes.inputLabel}>Phone Number</FormLabel>
                            <PatternFormat
                                id='phone'
                                name='phone'
                                customInput={TextField}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                format="(###) ###-####"
                                InputProps={{sx: classes.numberbox}}
                                defaultValue={paymentRequest.phone}
                                onValueChange={(values) => handlePhone(values)}
                                aria-invalid={hasValidationErrors()}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{paddingLeft: '0'}}>
                        <FormControlLabel
                            required
                            control={
                                <Checkbox
                                    name="tandc"
                                    color='primary'
                                    defaultChecked={false}
                                    onChange={(e) => {setAcceptTerms(e.target.checked)}}
                                />
                            }
                            label={ smallScreen ?
                                <Typography component="span" sx={{fontSize: '.9rem'}}>
                                    I accept the <Link tabIndex="0" onClick={() => {setOpenTandC(true)}} onKeyDown={handleKeyDown} href="#" aria-haspopup="dialog">Payment Terms</Link>
                                </Typography> :
                                <Typography component="span" sx={{fontSize: '1.2rem'}}>
                                    I agree to the <Link tabIndex="0" onClick={() => {setOpenTandC(true)}} onKeyDown={handleKeyDown} href="#" aria-haspopup="dialog">CitePayUSA Terms and Conditions</Link>
                                </Typography>
                            }
                            sx={{...classes.checkbox, marginTop: '1rem', marginLeft: '-2rem', paddingLeft: '0', lineHeight: smallScreen ? '.85rem' : ''}}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
}

export default CardData;
